<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const assets = ref([]);
const consents = ref([]);
const privacyEvents = ref([]);
const deletionQueue = ref([]);
const isLoading = ref(false);
const errorMessage = ref('');
const noticeMessage = ref('');
const selectedSection = ref('photos');

const originalAssets = computed(() =>
  assets.value.filter(asset => !asset.is_ai_generated)
);
const simulationAssets = computed(() =>
  assets.value.filter(asset => asset.is_ai_generated)
);

function formatDate(value) {
  if (!value) return 'Sem prazo';
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function purposeLabel(value) {
  const labels = {
    store_qualification_photo: 'Foto para qualificacao',
    visual_simulation: 'Simulacao visual',
    send_photo_to_ai_provider: 'Envio ao provedor de IA',
    marketing_use: 'Uso comercial',
  };
  return labels[value] || value;
}

function consentStatus(value) {
  const labels = {
    granted: 'Ativo',
    denied: 'Recusado',
    revoked: 'Revogado',
    expired: 'Expirado',
  };
  return labels[value] || value;
}

function eventLabel(value) {
  const labels = {
    consent_granted: 'Consentimento registrado',
    consent_denied: 'Consentimento recusado',
    consent_revoked: 'Consentimento revogado',
    media_upload_blocked: 'Upload bloqueado',
    media_uploaded: 'Foto armazenada',
    signed_url_issued: 'Foto visualizada pela equipe',
    sent_to_ai_provider: 'Foto enviada ao provedor de IA',
    simulation_generated: 'Simulacao gerada',
    simulation_delivery_failed: 'Falha ao enviar simulacao',
    deletion_requested: 'Exclusao solicitada',
    deletion_completed: 'Exclusao concluida',
    deletion_failed: 'Falha no expurgo',
  };
  return labels[value] || value;
}

async function fetchMedia() {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const params = new URLSearchParams({
      externalContactId: String(props.contactId),
    });
    const response = await fetch(`/api/superwoot/contact-media?${params}`);
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    assets.value = payload.mediaAssets || [];
    consents.value = payload.consents || [];
    privacyEvents.value = payload.privacyEvents || [];
    deletionQueue.value = payload.deletionQueue || [];
  } catch {
    assets.value = [];
    consents.value = [];
    privacyEvents.value = [];
    deletionQueue.value = [];
    errorMessage.value = 'Nao foi possivel carregar fotos e privacidade.';
  } finally {
    isLoading.value = false;
  }
}

async function requestDeletion(asset) {
  if (!window.confirm('Solicitar exclusao desta foto do cofre privado?')) return;
  noticeMessage.value = '';
  errorMessage.value = '';
  try {
    const response = await fetch(
      `/api/superwoot/contact-media?assetId=${encodeURIComponent(asset.id)}`,
      { method: 'DELETE' }
    );
    if (!response.ok) throw new Error('request_failed');
    noticeMessage.value = 'Exclusao solicitada. O expurgo sera auditado.';
    await fetchMedia();
  } catch {
    errorMessage.value = 'Nao foi possivel solicitar a exclusao.';
  }
}

watch(() => props.contactId, fetchMedia);
onMounted(fetchMedia);
</script>

<template>
  <div class="flex flex-col gap-4 px-6 py-5">
    <div>
      <h3 class="text-sm font-medium text-n-slate-12">Fotos e privacidade</h3>
      <p class="mt-1 text-xs text-n-slate-10">
        Cofre privado do contato
      </p>
    </div>

    <div class="grid grid-cols-3 gap-1">
      <button
        v-for="section in [
          { value: 'photos', label: 'Fotos' },
          { value: 'consents', label: 'Aceites' },
          { value: 'events', label: 'Trilha' },
        ]"
        :key="section.value"
        type="button"
        class="px-2 py-1 text-xs border rounded-md border-n-weak"
        :class="
          selectedSection === section.value
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-solid-2 text-n-slate-11'
        "
        @click="selectedSection = section.value"
      >
        {{ section.label }}
      </button>
    </div>

    <p v-if="isLoading" class="text-xs text-n-slate-10">Carregando...</p>
    <p v-if="errorMessage" class="text-xs text-n-ruby-11">{{ errorMessage }}</p>
    <p v-if="noticeMessage" class="text-xs text-n-teal-11">{{ noticeMessage }}</p>

    <template v-if="!isLoading && selectedSection === 'photos'">
      <p v-if="!assets.length" class="text-xs text-n-slate-10">
        Nenhuma foto privada armazenada para este contato.
      </p>

      <section v-if="originalAssets.length" class="flex flex-col gap-2">
        <h4 class="text-xs font-medium uppercase text-n-slate-10">Originais</h4>
        <article
          v-for="asset in originalAssets"
          :key="asset.id"
          class="overflow-hidden border rounded-md border-n-weak bg-n-solid-2"
        >
          <img :src="asset.preview_url" alt="Foto privada do contato" class="object-cover w-full h-32 bg-n-solid-3" />
          <div class="flex items-start justify-between gap-2 p-3">
            <div>
              <p class="text-xs font-medium text-n-slate-12">{{ purposeLabel(asset.purpose) }}</p>
              <p class="mt-1 text-xs text-n-slate-10">Validade: {{ formatDate(asset.expires_at) }}</p>
            </div>
            <Button icon="i-lucide-trash-2" size="sm" ruby @click="requestDeletion(asset)" />
          </div>
        </article>
      </section>

      <section v-if="simulationAssets.length" class="flex flex-col gap-2">
        <h4 class="text-xs font-medium uppercase text-n-slate-10">Simulacoes com IA</h4>
        <article
          v-for="asset in simulationAssets"
          :key="asset.id"
          class="overflow-hidden border rounded-md border-n-weak bg-n-solid-2"
        >
          <img :src="asset.preview_url" alt="Simulacao visual gerada por IA" class="object-cover w-full h-32 bg-n-solid-3" />
          <div class="p-3">
            <div class="flex items-start justify-between gap-2">
              <strong class="text-xs font-medium text-n-blue-11">Gerada por IA</strong>
              <Button icon="i-lucide-trash-2" size="sm" ruby @click="requestDeletion(asset)" />
            </div>
            <p class="mt-1 text-xs text-n-slate-10">{{ asset.ai_disclaimer_text }}</p>
            <p class="mt-1 text-xs text-n-slate-10">Validade: {{ formatDate(asset.expires_at) }}</p>
          </div>
        </article>
      </section>

      <section v-if="deletionQueue.length" class="flex flex-col gap-2">
        <h4 class="text-xs font-medium uppercase text-n-slate-10">Expurgo</h4>
        <p
          v-for="item in deletionQueue"
          :key="item.id"
          class="p-2 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-11"
        >
          {{ item.status }} · {{ formatDate(item.queued_at) }}
        </p>
      </section>
    </template>

    <section v-if="!isLoading && selectedSection === 'consents'" class="flex flex-col gap-2">
      <p v-if="!consents.length" class="text-xs text-n-slate-10">Nenhum consentimento registrado.</p>
      <article v-for="consent in consents" :key="consent.id" class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <strong class="block text-xs font-medium text-n-slate-12">{{ purposeLabel(consent.consent_purpose) }}</strong>
        <span class="block mt-1 text-xs text-n-slate-10">{{ consentStatus(consent.status) }} · {{ formatDate(consent.expires_at) }}</span>
      </article>
    </section>

    <section v-if="!isLoading && selectedSection === 'events'" class="flex flex-col gap-2">
      <p v-if="!privacyEvents.length" class="text-xs text-n-slate-10">Nenhum evento de privacidade registrado.</p>
      <article v-for="event in privacyEvents" :key="event.id" class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <strong class="block text-xs font-medium text-n-slate-12">{{ eventLabel(event.event_type) }}</strong>
        <span class="block mt-1 text-xs text-n-slate-10">{{ formatDate(event.created_at) }}</span>
      </article>
    </section>
  </div>
</template>
